import { appOverride } from '@util/AppUtils';
import { trackFlowEnteredAnalytics } from '@util/FlowUtils';
import { useQuery } from '@util/QueryParams';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { isDefined } from 'ts-utils';
import { chooseAssessmentConfig } from '../../config/ChooseAssessmentConfig';
import { Color } from '../../config/Color';
import { ActionType } from '../../store/ActionTypes';
import { useAppDispatch, useAppStore } from '../../store/Store';
import '../../theme.less';
import { animations } from '../assets/Animations';
import { PreloadFlowImages } from '../assets/preloadFlowImages/PreloadFlowImages';
import { FlowStack } from '../flows/FlowStack';
import { LoadingSpinner } from '../styles/LoadingSpinner';

export const Onboard: FunctionComponent = () => {
  const purchase = useAppStore((state) => state.purchase);
  const user = useAppStore((state) => state.user);
  const entry = useAppStore((state) => state.entry);
  const flowConfig = useAppStore(chooseAssessmentConfig);
  const dispatch = useAppDispatch();
  const history = useHistory();
  const [backdropColor, setBackdropColor] = useState<undefined | string>(undefined);
  const authToken = useQuery().get('token') ?? undefined;
  const background = appOverride({ nerva: animations.nervaBackground, default: undefined });

  useEffect(
    function trackAnalyticsEvent() {
      trackFlowEnteredAnalytics(purchase, flowConfig.name);
    },
    [flowConfig.name]
  );

  useEffect(
    function signInWithQueryParamToken() {
      if (authToken !== undefined) {
        dispatch({
          type: ActionType.LOGIN_USER,
          details: { token: authToken },
        });
      }
    },
    [authToken]
  );

  useEffect(
    function redirectIfHasAccessCode() {
      const { referral, aslPaymentLinkId } = entry;
      if (referral === undefined || referral.isAslReferral || aslPaymentLinkId !== undefined)
        return;
      if (referral.hasUserId && referral.hasAccessCode) {
        const currentParams = new URLSearchParams(window.location.search);
        const isLocal = window.location.hostname === 'localhost';
        const downloadAppPath = `${isLocal ? '/nerva' : ''}/youHaveAccess`;
        history.replace(`${downloadAppPath}?${currentParams.toString()}`);
      }
    },
    [entry, history]
  );

  // Entry is resolved before this renders, so only a sign-in from the URL token can still be loading.
  const shouldShowLoading = authToken !== undefined && user.loading;

  return (
    <Backdrop color={backdropColor ?? Color.surface.default}>
      {isDefined(background) && <Background src={background} alt="" />}
      <ContentWrapper>
        {shouldShowLoading ? (
          <LoadingSpinner />
        ) : (
          <>
            <PreloadFlowImages flowConfig={flowConfig} />
            <FlowStack config={flowConfig} onChangeBackdropColor={setBackdropColor} />
          </>
        )}
      </ContentWrapper>
    </Backdrop>
  );
};

type BackdropProps = {
  color?: string | undefined;
};

const Backdrop = styled.div<BackdropProps>`
  position: relative;
  height: 100%;
  min-height: 100vh;
  background: ${({ color }) => color ?? Color.surface.default};
  transition: 0.3s background-color ease-in-out;
  /* clip rather than hidden: hidden makes this a scroll container, which stops the sticky header
     from sticking. Clipping is visually identical without that side effect. */
  overflow: clip;
`;

const Background = styled.img`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 0;
  pointer-events: none;
  object-fit: cover;
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 1;
  height: 100%;
  min-height: 100vh;
`;
